/* global runtime */
// Rapporter, kontoplan og budsjettmodeller.
// Lastes som klassisk script før backend.jsx og deler dens bindingsmiljø.
const cachedYearReports = (state, year) =>
  cachedDerivedModel(`reports:${year}::::1:1:1`, () => deriveReports(state, year));
const getReportsModel = (options = {}) => {
  if (!runtime.appState || (!runtime.configured && !runtime.user)) return null;
  const state = mergedRuntimeState();
  const year = Number(options.year || state.activeYear) || new Date().getFullYear();
  const accountingBasis = state.accountingBasisCoverage;
  const reportPeriod = options.fiscalYear ? window.PlummeTrialBalanceContinuity.fiscalBounds(state.settings, year) : { from: `${year}-01-01`, to: `${year}-12-31` };
  if (!ledgerBalancesFor(state, year, { ...options, fiscalBounds: options.fiscalYear ? reportPeriod : undefined }) && (!state.accountingJournalDeferred || state.accountingReadModelCoverage || reportDimensionApi().active(options) || !monthAligned(options))) window.PlummeJournalDemand.request();
  if (Array.isArray(state.accountingEvents) && !(window.PlummeAccountingBasisCoverage.isLoading(accountingBasis) && ledgerBalancesFor(state, year, { ...options, fiscalBounds: options.fiscalYear ? reportPeriod : undefined })) && window.PlummeAccountingBasisCoverage.isBlocked(accountingBasis, { period: reportPeriod, allowImportedStatistics: true })) return window.PlummeAccountingBasisCoverage.blockedReports(accountingBasis);
  // Rapportskjermene bytter periode fram og tilbake — maned for maned, ar mot ar. Hver
  // kombinasjon caches for seg, sa det aa gaa tilbake til en periode du allerede har
  // sett ikke koster noe.
  const key = [
    'reports', year, options.fiscalYear ? 'fiscal' : 'calendar',
    Array.isArray(options.months) ? options.months.join('-') : '',
    options.from || '', options.to || '',
    options.includeChart === false ? 0 : 1,
    options.includeResult === false ? 0 : 1,
    options.includeTrial === false ? 0 : 1,
    options.projectId || '', options.departmentId || '',
  ].join(':');
  const reports = cachedDerivedModel(key, () => deriveReports(state, year, options));
  return accountingBasis?.reporting_basis?.kind === 'imported_accounting_statistics'
    ? { ...reports, reportingBasis: accountingBasis.reporting_basis }
    : reports;
};
const downloadReportPdf = async (report) => {
  const payload = await authorizedPost('sendReportPdfEmailHttp', { action: 'download', report });
  if (!payload?.pdfBase64 || !payload?.fileName) throw new Error(t('svc.pdfFailed'));
  return { pdfBase64: payload.pdfBase64, fileName: payload.fileName };
};
const getChartAccountsModel = () => {
  const state = mergeState(runtime.appState);
  return mergePlumme1ChartAccounts(state.chartAccounts, state.settings);
};

const saveCustomChartAccount = async (input = {}) => {
  let savedAccount = null;
  await updateStateAndPersist((state) => {
    const code = String(input.code || input.accountNumber || '').replace(/\D/g, '').slice(0, 5);
    const name = String(input.name || input.label || '').trim();
    const equivalentCode = String(input.equivalentCode || input.standardAccountCode || '').replace(/\D/g, '').slice(0, 4);
    if (!/^[1-8]\d{3,4}$/.test(code)) throw new Error(t('r1e.budget.codeFormat'));
    if (!name) throw new Error(t('r1e.budget.nameRequired'));
    const standardAccount = buildStandardChartAccounts(state.settings).find((account) => account.code === equivalentCode);
    if (!standardAccount) throw new Error(t('r1e.budget.pickStandard'));
    if (backendProductAccountExists(state, code)) throw new Error(t('r1e.budget.accountExists', { code }));
    savedAccount = stripUndefinedDeep({
      id: `acct-${code}`,
      code,
      name,
      category: input.category || standardAccount.category || chartAccountCategory(code),
      country: normalizeChartCountry(state.settings),
      active: true,
      locked: false,
      custom: true,
      equivalentCode,
      standardAccountCode: equivalentCode,
      yearClosingStandardAccountCode: equivalentCode,
      mapsToAccountCode: equivalentCode,
      createdAt: nowIso(),
      createdBy: 'manual',
    });
    return { ...state, chartAccounts: [...safeArray(state.chartAccounts), savedAccount] };
  });
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
  return savedAccount;
};
const saveCustomChartAccountMappings = async (mappings = {}) => {
  let updated = 0;
  await updateStateAndPersist((state) => {
    const standards = new Set(buildStandardChartAccounts(state.settings).map((account) => account.code));
    const nextAccounts = safeArray(state.chartAccounts).map((account) => {
      const code = String(account.code || account.accountNumber || account.id || '').replace(/\D/g, '').slice(0, 5);
      const equivalentCode = String(mappings[code] || '').replace(/\D/g, '').slice(0, 4);
      if (!account?.custom || !equivalentCode) return account;
      if (!standards.has(equivalentCode)) throw new Error(t('r1e.budget.standardMissing', { code: equivalentCode }));
      updated += 1;
      return { ...account, custom: true, equivalentCode, standardAccountCode: equivalentCode, yearClosingStandardAccountCode: equivalentCode, mapsToAccountCode: equivalentCode, updatedAt: nowIso() };
    });
    return { ...state, chartAccounts: nextAccounts };
  });
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
  return { updated };
};

const budgetRound = (amount) => Math.round((Number(amount) || 0) * 100) / 100;
const emptyBudgetMonths = () => Array.from({ length: 12 }, () => 0);
const distributeBudgetAmount = (amount) => {
  const monthly = Math.max(0, num(amount)) / 12;
  return emptyBudgetMonths().map(() => monthly);
};
const normalizeBudgetMonthlyAmounts = (line = {}) => {
  const source = Array.isArray(line.monthlyAmounts) ? line.monthlyAmounts : distributeBudgetAmount(line.annualAmount);
  return emptyBudgetMonths().map((_, index) => Math.max(0, num(source[index])));
};
const budgetLineId = (kind, accountCode) => `${kind}-${accountCode}`;
const getBudgetTotalsFromLines = (lines = []) => {
  const income = budgetRound(safeArray(lines).filter((line) => line.kind === 'income').reduce((sum, line) => sum + Math.max(0, num(line.annualAmount)), 0));
  const costs = budgetRound(safeArray(lines).filter((line) => line.kind === 'cost').reduce((sum, line) => sum + Math.max(0, num(line.annualAmount)), 0));
  return { income, costs, result: budgetRound(income - costs) };
};
const getBudgetMonthlyTotalsFromLines = (lines = []) => {
  const incomeMonthly = emptyBudgetMonths();
  const costMonthly = emptyBudgetMonths();
  safeArray(lines).forEach((line) => {
    const target = line.kind === 'income' ? incomeMonthly : costMonthly;
    normalizeBudgetMonthlyAmounts(line).forEach((amount, index) => {
      target[index] += amount;
    });
  });
  return {
    incomeMonthly: incomeMonthly.map(budgetRound),
    costMonthly: costMonthly.map(budgetRound),
  };
};
const getBudgetAnnualTotals = (budget) => {
  if (safeArray(budget?.lines).length) return getBudgetTotalsFromLines(budget.lines);
  const income = budgetRound(safeArray(budget?.incomeMonthly).reduce((sum, amount) => sum + Math.max(0, num(amount)), 0));
  const costs = budgetRound(safeArray(budget?.costMonthly).reduce((sum, amount) => sum + Math.max(0, num(amount)), 0));
  return { income, costs, result: budgetRound(income - costs) };
};
const createBudgetPlanFromLines = ({ year, lines, source = 'manual', note }) => {
  const safeLines = safeArray(lines).filter((line) => Math.max(0, num(line.annualAmount)) > 0);
  const totals = getBudgetTotalsFromLines(safeLines);
  const monthly = getBudgetMonthlyTotalsFromLines(safeLines);
  return stripUndefinedDeep({
    year,
    source,
    incomeMonthly: monthly.incomeMonthly,
    costMonthly: monthly.costMonthly,
    lines: safeLines,
    note,
    updatedAt: nowIso(),
    totals,
  });
};
const getBudgetAccountName = (state, accountCode) =>
  trialAccount(state, accountCode)?.name || plumme1AccountName(accountCode, `Konto ${accountCode}`, state?.settings);
// Faktisk-linjene akkumuleres per maned naar kilden vet hvilken maned belopet horer
// til. Belop uten maned — startforslaget, og importer som bare har en saldobalanse
// uten hovedbok — legges i `unallocated` og fordeles jevnt til slutt. Da beholder en
// importert hovedbok sin virkelige sesong i stedet for aa bli tolv like store maneder.
const addBudgetActualLine = (totals, state, kind, accountCode, amount, monthIndex = null, accountName = '') => {
  const cleanCode = String(accountCode || '').trim();
  const signedAmount = num(amount);
  if (!cleanCode || Math.abs(signedAmount) <= 0.005) return;
  const id = budgetLineId(kind, cleanCode);
  const current = totals.get(id) || {
    id,
    accountCode: cleanCode,
    accountName: String(accountName || '').trim() || getBudgetAccountName(state, cleanCode),
    kind,
    annualAmount: 0,
    monthlyAmounts: emptyBudgetMonths(),
    unallocated: 0,
  };
  current.annualAmount += signedAmount;
  const index = Number.isInteger(monthIndex) && monthIndex >= 0 && monthIndex < 12 ? monthIndex : null;
  if (index === null) current.unallocated += signedAmount;
  else current.monthlyAmounts[index] += signedAmount;
  totals.set(id, current);
};
// En konto som summerer seg til null over aaret — en postering og reverseringen av den
// — skal ikke bli staaende som en tom rad. Det var `totals.delete` sin jobb for, og maa
// gjores her naa som summen forst er kjent etter at alle bidrag er lagt inn.
const finalizeBudgetActualLines = (totals) => [...totals.values()]
  .map((line) => {
    const spread = num(line.unallocated) / 12;
    return {
      id: line.id,
      accountCode: line.accountCode,
      accountName: line.accountName,
      kind: line.kind,
      annualAmount: budgetRound(line.annualAmount),
      monthlyAmounts: line.monthlyAmounts.map((amount) => budgetRound(amount + spread)),
    };
  })
  .filter((line) => Math.abs(line.annualAmount) > 0.005)
  .sort((a, b) => a.kind === b.kind ? a.accountCode.localeCompare(b.accountCode, 'nb-NO') : (a.kind === 'income' ? -1 : 1));
const budgetActualMonthIndex = (entry) => {
  const month = Number(nbToIso(entry?.date || entry?.createdAt).slice(5, 7)) - 1;
  return month >= 0 && month < 12 ? month : null;
};
// Importert regnskap ligger som konto x maned i `accountingImportReportStatsByYear`.
// Har importen hovedbok gir `monthly` den ekte fordelingen; er det bare en saldobalanse
// finnes arsbevegelsen pa `accounts` og maneden er ukjent. Funksjonen er ren og tar hele
// stats-objektet, sa den kan testes uten a bygge en appState.
const importedBudgetActualRows = (stats) => {
  const names = new Map(safeArray(stats?.accounts)
    .map((account) => [String(account?.accountCode || '').trim(), account?.name])
    .filter(([code]) => code));
  const rows = new Map();
  const rowFor = (code) => {
    const current = rows.get(code) || { accountCode: code, name: names.get(code) || '', monthly: emptyBudgetMonths(), annual: 0, hasMonthly: false };
    rows.set(code, current);
    return current;
  };
  safeArray(stats?.monthly).forEach((entry) => {
    const code = String(entry?.accountCode || '').trim();
    const monthIndex = Number(entry?.month) - 1;
    if (!code || isImportedClosingTransferAccountCode(code) || monthIndex < 0 || monthIndex >= 12) return;
    const row = rowFor(code);
    row.monthly[monthIndex] += num(entry?.movement);
    row.annual += num(entry?.movement);
    row.hasMonthly = true;
  });
  safeArray(stats?.accounts).forEach((account) => {
    const code = String(account?.accountCode || '').trim();
    if (!code || isImportedClosingTransferAccountCode(code)) return;
    if (rows.get(code)?.hasMonthly) return;
    const movement = num(account?.movement);
    if (Math.abs(movement) <= 0.005) return;
    const row = rowFor(code);
    row.annual += movement;
  });
  return [...rows.values()];
};
// Aarsakskjeden bak fortegnet er den samme som i `addImportedReportStatsToReport`:
// inntektskonti staar i kredit og har negativ bevegelse. Finanskonti (8xxx) holdes
// utenfor, fordi budsjettgrafene ogsa kjorer uten finansposter.
const addImportedBudgetActualLines = (totals, state, year) => {
  importedBudgetActualRows(importedReportStatsForYear(state, year)).forEach((row) => {
    if (isFinanceAccountCode(row.accountCode)) return;
    const isIncome = isReportIncomeAccountCode(state, row.accountCode);
    if (!isIncome && !isReportCostAccountCode(state, row.accountCode)) return;
    const sign = isIncome ? -1 : 1;
    const kind = isIncome ? 'income' : 'cost';
    if (row.hasMonthly) {
      row.monthly.forEach((movement, monthIndex) =>
        addBudgetActualLine(totals, state, kind, row.accountCode, sign * movement, monthIndex, row.name));
      return;
    }
    addBudgetActualLine(totals, state, kind, row.accountCode, sign * row.annual, null, row.name);
  });
};
const budgetSalesAccountCode = (state, lineOrSale) => reportSalesAccountCode(state, lineOrSale);
const getActualBudgetLines = (state, year) => {
  const totals = new Map();
  safeArray(state.invoices)
    .filter((invoice) => isPostedInvoice(invoice, state) && entryYear(invoice) === year)
    .forEach((invoice) => {
      const monthIndex = budgetActualMonthIndex(invoice);
      safeArray(invoice.lines).forEach((line) => {
        addBudgetActualLine(totals, state, 'income', budgetSalesAccountCode(state, line), invoiceLineNet(line) * invoicePostingFactor(invoice), monthIndex);
      });
    });
  safeArray(state.otherSales)
    .filter((sale) => isActiveOtherSale(sale) && entryYear(sale) === year && sale.vatTreatment !== 'vatOnly')
    .forEach((sale) => {
      globalThis.PlummeOtherSaleAmounts.groups(sale).forEach((group) => addBudgetActualLine(totals, state, 'income', budgetSalesAccountCode(state, group), group.amountNet, budgetActualMonthIndex(sale)));
    });
  safeArray(state.expenses)
    .filter((expense) => isActiveExpense(expense) && entryYear(expense) === year && !isFixedAssetExpense(expense))
    .forEach((expense) => {
      const monthIndex = budgetActualMonthIndex(expense);
      if (expense.manualPosting && safeArray(expense.manualPostingLines).length) {
        expensePostings(expense).forEach((line) => {
          if (isCostAccountCode(line.nr)) addBudgetActualLine(totals, state, 'cost', line.nr, Math.max(0, num(line.amount)), monthIndex);
          if (isIncomeAccountCode(line.nr)) addBudgetActualLine(totals, state, 'income', line.nr, Math.max(0, -num(line.amount)), monthIndex);
        });
        return;
      }
      const lines = safeArray(expense.purchaseLines).length ? safeArray(expense.purchaseLines) : [expense];
      const lineTotal = lines.reduce((sum, line) => sum + Math.max(0, num(line.amountNet, expense.amountNet)), 0);
      const costAmount = dashboardCostExpense(expense);
      lines.forEach((line) => {
        const amountNet = Math.max(0, num(line.amountNet, expense.amountNet));
        const share = lineTotal > 0 ? (amountNet / lineTotal) * costAmount : costAmount;
        addBudgetActualLine(totals, state, 'cost', line.costAccountCode || expense.costAccountCode || '6300', share, monthIndex);
      });
    });
  addImportedBudgetActualLines(totals, state, year);
  return finalizeBudgetActualLines(totals);
};
// Manedstallene skal vare de samme som Hjem og Rapporter viser. `deriveReportChart`
// folder inn importert regnskap; den gamle `deriveChart` (na fjernet) sa bare bilag i
// Plumme, og lot derfor budsjettsiden sta tom etter en import.
const getActualBudgetMonthlyTotals = (state, year) => {
  const chart = addImportedSalesHistoryToDashboardChart(deriveReportChart(state, year), state, year);
  const incomeMonthly = chart.map((month) => budgetRound(month.inc));
  const costMonthly = chart.map((month) => budgetRound(month.cost));
  return {
    incomeMonthly,
    costMonthly,
    income: budgetRound(incomeMonthly.reduce((sum, amount) => sum + amount, 0)),
    costs: budgetRound(costMonthly.reduce((sum, amount) => sum + amount, 0)),
    result: budgetRound(incomeMonthly.reduce((sum, amount) => sum + amount, 0) - costMonthly.reduce((sum, amount) => sum + amount, 0)),
  };
};
const createBudgetFromPreviousYear = (state, year) =>
  createBudgetPlanFromLines({
    year,
    lines: getActualBudgetLines(state, year - 1),
    source: 'prefilled',
    note: `Basert p\u00e5 faktiske tall fra ${year - 1}.`,
  });
const createStarterBudgetSuggestion = (state, year, input = {}) => {
  const lines = new Map();
  addBudgetActualLine(lines, state, 'income', '3000', input.expectedIncome);
  addBudgetActualLine(lines, state, 'cost', '4300', input.goodsAnnual);
  addBudgetActualLine(lines, state, 'cost', '6300', num(input.officeMonthly) * 12);
  addBudgetActualLine(lines, state, 'cost', '6540', num(input.softwareMonthly) * 12);
  addBudgetActualLine(lines, state, 'cost', '6700', num(input.accountingMonthly) * 12);
  addBudgetActualLine(lines, state, 'cost', '7140', input.travelAnnual);
  addBudgetActualLine(lines, state, 'cost', '7330', input.marketingAnnual);
  addBudgetActualLine(lines, state, 'cost', '7700', input.otherAnnual);
  return createBudgetPlanFromLines({
    year,
    lines: finalizeBudgetActualLines(lines),
    source: 'prefilled',
    note: 'Startforslag basert p\u00e5 svar i budsjettveiviseren.',
  });
};
const normalizeBudgetLine = (state, line) => {
  const kind = line.kind === 'income' || line.type === 'inntekt' ? 'income' : 'cost';
  const accountCode = String(line.accountCode || line.nr || '').trim();
  const annualAmount = Math.max(0, num(line.annualAmount ?? line.year));
  return stripUndefinedDeep({
    id: line.id || budgetLineId(kind, accountCode || createId('budget-line')),
    accountCode,
    accountName: String(line.accountName || line.name || getBudgetAccountName(state, accountCode)).trim(),
    kind,
    annualAmount,
    monthlyAmounts: normalizeBudgetMonthlyAmounts({ ...line, annualAmount, monthlyAmounts: line.monthlyAmounts || line.months }),
  });
};
const saveBudgetPlan = async ({ year, lines, source = 'manual', note } = {}) => {
  const state = mergeState(runtime.appState);
  const activeYear = Number(year || state.activeYear) || new Date().getFullYear();
  const normalizedLines = safeArray(lines).map((line) => normalizeBudgetLine(state, line)).filter((line) => line.accountCode && line.annualAmount > 0);
  const budget = createBudgetPlanFromLines({ year: activeYear, lines: normalizedLines, source, note });
  await updateStateAndPersist((current) => ({
    ...current,
    budgetsByYear: {
      ...(current.budgetsByYear || {}),
      [String(activeYear)]: budget,
    },
  }));
  return getBudgetModel({ year: activeYear });
};
const generateBudgetSuggestion = async ({ question, year } = {}) => {
  const state = mergeState(runtime.appState);
  return authorizedPost('generateBudgetSuggestionHttp', {
    businessId: runtime.activeBusinessId,
    question,
    year: Number(year || state.activeYear) || new Date().getFullYear(),
    locale: uiLocale(),
  });
};
const getBudgetModel = ({ year } = {}) => {
  if (!runtime.appState || (!runtime.configured && !runtime.user)) return null;
  const state = mergeState(runtime.appState);
  const activeYear = Number(year || state.activeYear) || new Date().getFullYear();
  const budget = state.budgetsByYear?.[String(activeYear)] || null;
  const actuals = getActualBudgetMonthlyTotals(state, activeYear);
  const actualLines = getActualBudgetLines(state, activeYear);
  const budgetLines = safeArray(budget?.lines).map((line) => normalizeBudgetLine(state, line));
  const budgetTotals = budgetLines.length ? getBudgetTotalsFromLines(budgetLines) : getBudgetAnnualTotals(budget);
  const budgetMonthly = budgetLines.length
    ? getBudgetMonthlyTotalsFromLines(budgetLines)
    : {
        incomeMonthly: safeArray(budget?.incomeMonthly).length ? safeArray(budget.incomeMonthly).map(budgetRound) : distributeBudgetAmount(budgetTotals.income),
        costMonthly: safeArray(budget?.costMonthly).length ? safeArray(budget.costMonthly).map(budgetRound) : distributeBudgetAmount(budgetTotals.costs),
      };
  const previousYearLines = getActualBudgetLines(state, activeYear - 1);
  return {
    year: activeYear,
    budget,
    budgetLines,
    budgetTotals,
    budgetMonthly,
    actuals,
    actualLines,
    previousYearLines,
    accountOptions: mergePlumme1ChartAccounts(state.chartAccounts, state.settings)
      .filter((account) => account.active !== false && (account.category === 'income' || account.category === 'cost'))
      .map((account) => ({ code: account.code, name: account.name, category: account.category })),
    availableYears: [...new Set([activeYear, activeYear - 1, activeYear + 1])].sort((a, b) => a - b),
  };
};
